import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Page } from "@boundary/layouts/pageLayout";
import { BonusBadge } from "@boundary/components/bonusBadge/bonusBadge";
import { WelcomeGift } from "@boundary/components/welcomeGift/welcomeGift";
import { useFunnelStep, useMachineStore } from "@boundary/_hooks";
import { OrderFormService } from "@domain/services/orderForm.service";
import { MapService } from "@domain/services/map.service";
import { LogoBW } from "@boundary/components/phodoLogos";
import { ActiveOrdersCarousel } from "@boundary/components/activeOrdersCarousel";
import { MapActionBar } from "@boundary/widgets/mapActionBar";
import { MapActionCard } from "@boundary/widgets/mapActionCard";
import { MachineDetailsWidget } from "@boundary/widgets/machineDetailsWidget";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import styles from "./map.module.scss";

const LAT_OFFSET_FOR_WIDGET = 0.002;
const INITIAL_ZOOM = 15;

export function MapPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const machineIdFromUrl = searchParams.get("machineId");
  const { items, selectItem } = useMachineStore();
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
  const isClosingRef = useRef(false);

  useFunnelStep("map_view");

  // Обработка machineId из URL
  useEffect(() => {
    // Игнорируем, если мы закрываем виджет программно
    if (isClosingRef.current) {
      return;
    }

    if (machineIdFromUrl && items.length > 0) {
      const machineId = Number(machineIdFromUrl);
      if (selectedMachineId !== machineId) {
        const item = items.find((i) => i.id === machineId);
        if (item) {
          selectItem(machineId);
          setSelectedMachineId(machineId);
          if (item.latitude !== null && item.longitude !== null) {
            MapService.setLocation({
              center: [item.longitude, item.latitude],
              zoom: 16
            });
          }
        }
      }
    } else if (!machineIdFromUrl && selectedMachineId !== null) {
      // Если machineId удален из URL, закрываем виджет
      setSelectedMachineId(null);
      selectItem(null);
    }
  }, [machineIdFromUrl, items, selectedMachineId, selectItem]);

  // Обработка popstate для закрытия деталей аппарата
  useEffect(() => {
    if (selectedMachineId === null) return;

    const handlePopState = () => {
      setSelectedMachineId(null);
      selectItem(null);
      if (machineIdFromUrl) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("machineId");
        setSearchParams(newSearchParams, { replace: true });
      }
    };

    if (machineIdFromUrl) {
      window.history.pushState({ machineDetailsOpen: true }, "", window.location.href);
    }

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [selectedMachineId, machineIdFromUrl, searchParams, setSearchParams, selectItem]);

  const handleGeoClick = useCallback(async () => {
    const location = await MapService.getCurrentLocation();
    if (location) {
      MapService.setLocation({
        center: [location.longitude, location.latitude + LAT_OFFSET_FOR_WIDGET],
        zoom: INITIAL_ZOOM
      });
    }
  }, []);

  const handleCloseMachineDetails = useCallback(() => {
    // Устанавливаем флаг, что мы закрываем виджет программно
    isClosingRef.current = true;
    
    // Сначала удаляем machineId из URL
    if (machineIdFromUrl) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("machineId");
      setSearchParams(newSearchParams, { replace: true });
    }
    
    // Затем закрываем виджет
    setSelectedMachineId(null);
    selectItem(null);
    
    // Сбрасываем флаг после небольшой задержки
    setTimeout(() => {
      isClosingRef.current = false;
    }, 100);
  }, [selectItem, machineIdFromUrl, searchParams, setSearchParams]);

  const handleCreateOrder = useCallback((machineId: number) => {
    setSelectedMachineId(null);
    selectItem(null);
    const orderStateForMachine = OrderFormService.getOrder(machineId);
    const hasServicesInCart = orderStateForMachine.cart.services.length > 0;
    if (hasServicesInCart) {
      prefetchAndNavigate(navigate, `/cart?machineId=${machineId}`);
    } else {
      prefetchAndNavigate(navigate, `/catalog?machineId=${machineId}`);
    }
  }, [navigate, selectItem]);

  return (
    <Page screenBackground="none" className={styles.mapPage}>
      <Page.SafeArea />
      <Page.DisplayZone className={styles.mapDisplayZone}>
        <div className={styles.mapFade} />
        <div className={styles.logo}>
                  <LogoBW size="medium" />
                </div>
        <div className={styles.bonusBadge}>
          <BonusBadge />
        </div>
        {selectedMachineId === null ? (
          <div className={styles.bottomStack}>
            <div className={styles.welcomeGift}>
              <WelcomeGift />
            </div>
            <div className={styles.mapActionBar}>
              <MapActionBar onRightClick={handleGeoClick} />
            </div>
            <div className={styles.activeOrdersCarousel}>
                  <ActiveOrdersCarousel />
                </div>
            <div className={styles.mapActionCard}>
                  <MapActionCard />
                </div>
              </div>
          ) : (
          <div className={styles.machineDetailsWidget}>
              <MachineDetailsWidget 
                key={selectedMachineId}
                machineId={selectedMachineId} 
                onClose={handleCloseMachineDetails}
                onCreateOrder={handleCreateOrder}
              />
            </div>
          )}
      </Page.DisplayZone>
    </Page>
  );
}
